/**
 * Cura — Find your solution (collection tabs)
 * Fetches collection products via /collections/handle/products.json
 * Renders cards client-side. Caches per handle.
 */
(function () {
  'use strict';

  class CuraFindSolution {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      this.tabs = Array.from(section.querySelectorAll('[data-fs-tab]'));
      this.panel = section.querySelector('[data-fs-panel]');
      this.cache = {};

      this.metaCache = {};
      const metaScript = document.getElementById('cura-fs-meta-' + this.sectionId);
      if (metaScript) {
        try { this.metaCache = JSON.parse(metaScript.textContent); } catch(e) {}
      }

      // Cache first tab's server-rendered HTML
      const firstActive = this.tabs.find((t) => t.classList.contains('is-active'));
      if (firstActive) {
        this.cache[firstActive.dataset.handle] = { type: 'html', content: this.panel.innerHTML };
      }

      this.bindPanelWhenReady(this.panel);
      this.bind();
    }

    bindPanelWhenReady(panel, attempts) {
      attempts = attempts || 0;
      if (window.CuraBestsellersInit) {
        window.CuraBestsellersInit(panel);
        this.revealCards(panel);
      } else if (attempts < 50) {
        setTimeout(() => this.bindPanelWhenReady(panel, attempts + 1), 50);
      } else {
        this.revealCards(panel);
      }
    }

    revealCards(panel) {
      panel.querySelectorAll('[data-animate]').forEach(function (el) {
        el.classList.add('is-visible');
      });
    }

    bind() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab));
      });
    }

    select(tab) {
      if (tab.classList.contains('is-active')) return;
      const handle = tab.dataset.handle;

      this.tabs.forEach((t) => {
        const active = t === tab;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      if (this.cache[handle]) {
        const cached = this.cache[handle];
        if (cached.type === 'html') {
          this.swapHtml(cached.content);
        } else {
          this.renderProducts(cached.content, handle);
        }
        return;
      }

      this.panel.classList.add('is-loading');
      const productsPerTab = parseInt(this.section.dataset.productsPerTab || '4', 10);
      // Add timestamp to bust any CDN cache
      const url = '/collections/' + handle + '/products.json?limit=' + productsPerTab + '&_=' + Date.now();

      fetch(url, { headers: { 'Accept': 'application/json' }, credentials: 'include' })
        .then((r) => {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then((data) => {
          const products = (data.products || []).slice(0, productsPerTab);
          if (!products.length) {
            this.panel.classList.remove('is-loading');
            this.panel.innerHTML = '<p class="cura-fs__empty">No products in this collection yet.</p>';
            return;
          }
          products.forEach((p) => {
            p._meta = this.metaCache[p.handle] || {};
          });
          this.cache[handle] = { type: 'products', content: products };
          this.panel.classList.remove('is-loading');
          this.renderProducts(products, handle);
        })
        .catch((err) => {
          console.error('[CuraFS] fetch failed:', err);
          this.panel.classList.remove('is-loading');
          this.panel.innerHTML = '<p class="cura-fs__empty">Could not load products. Please try again.</p>';
        });
    }

    swapHtml(html) {
      this.panel.innerHTML = html;
      this.bindPanelWhenReady(this.panel);
      document.dispatchEvent(new CustomEvent('cura:content-loaded', { detail: { container: this.panel } }));
    }

    renderProducts(products, handle) {
      const collectionUrl = '/collections/' + handle;
      const showViewAll = this.section.dataset.showViewAll === 'true';
      const viewAllLabel = this.section.dataset.viewAllLabel || 'View All';

      let html = '<div class="cura-fs__grid-wrap"><ul class="cura-fs__grid" role="list">';

      products.forEach((product) => {
        const variant = product.variants && product.variants[0];
        if (!variant) return;

        const price = this.formatMoney(variant.price);
        const hasCompare = variant.compare_at_price
          && parseFloat(variant.compare_at_price) > parseFloat(variant.price);
        const comparePrice = hasCompare ? this.formatMoney(variant.compare_at_price) : '';
        const discount = hasCompare
          ? Math.round((1 - parseFloat(variant.price) / parseFloat(variant.compare_at_price)) * 100) + '% off'
          : '';
        const imageSrc = product.images && product.images[0]
          ? product.images[0].src.split('?')[0] + '?width=600'
          : '';

        const variantPills = product.variants && product.variants.length > 1
          ? '<div class="cura-bsg-card__variants" role="radiogroup" aria-label="Choose an option" data-variants>' +
            product.variants.map((v, vi) => {
              const vPrice = this.formatMoney(v.price);
              const vHasCompare = v.compare_at_price && parseFloat(v.compare_at_price) > parseFloat(v.price);
              const vCompare = vHasCompare ? this.formatMoney(v.compare_at_price) : '';
              return `<button type="button"
                class="cura-bsg-card__variant${vi === 0 ? ' is-selected' : ''}"
                data-variant-id="${v.id}"
                data-variant-price="${v.price}"
                data-variant-price-formatted="${vPrice}"
                data-variant-compare="${v.compare_at_price || ''}"
                data-variant-compare-formatted="${vCompare}"
                data-variant-available="true"
                role="radio"
                aria-checked="${vi === 0 ? 'true' : 'false'}"
              >${this.escHtml(v.title)}</button>`;
            }).join('') +
            '</div>'
          : '';

        html += `
          <li class="cura-fs__cell">
            <div class="cura-bsg-card" data-product-card
              data-product-url="/products/${product.handle}"
              data-product-handle="${product.handle}"
              data-variant-id="${variant.id}"
              data-variant-price-formatted="${price}"
              data-variant-compare-formatted="${comparePrice}"
            >
              <a href="/products/${product.handle}" class="cura-bsg-card__media" tabindex="-1" aria-hidden="true">
                ${imageSrc
                  ? `<img src="${imageSrc}" alt="${this.escHtml(product.title)}" loading="lazy" width="600">`
                  : ''}
              </a>
              <div class="cura-bsg-card__body">
                ${product._meta && product._meta.key_herb
                  ? `<span class="cura-bsg-card__herb">Key herb: ${this.escHtml(product._meta.key_herb)}</span>`
                  : ''}
                <h3 class="cura-bsg-card__title">
                  <a href="/products/${product.handle}" class="cura-bsg-card__title-link">${this.escHtml(product.title)}</a>
                </h3>
                ${product._meta && product._meta.short_description
                  ? `<p class="cura-bsg-card__desc">${this.escHtml(product._meta.short_description)}</p>`
                  : ''}
                ${product._meta && product._meta.tagline
                  ? `<p class="cura-bsg-card__tagline">${this.escHtml(product._meta.tagline)}</p>`
                  : ''}
                ${variantPills}
                <div class="cura-bsg-card__price-row">
                  <div class="cura-bsg-card__prices">
                    <span class="cura-bsg-card__price" data-price>${price}</span>
                    <span class="cura-bsg-card__compare" data-compare ${hasCompare ? '' : 'hidden'}>${comparePrice}</span>
                    <span class="cura-bsg-card__discount" data-discount ${hasCompare ? '' : 'hidden'}>${discount}</span>
                  </div>
                  <div class="cura-bsg-card__stepper" data-stepper>
                    <button type="button" class="cura-bsg-card__step" data-step-down aria-label="Decrease quantity">−</button>
                    <input type="number" class="cura-bsg-card__qty" data-qty value="1" min="1" inputmode="numeric" aria-label="Quantity">
                    <button type="button" class="cura-bsg-card__step" data-step-up aria-label="Increase quantity">+</button>
                  </div>
                </div>
                <button type="button" class="cura-bsg-card__atc button" data-add-to-cart
                  data-variant-id="${variant.id}">
                  <span class="cura-bsg-card__atc-label">Add to cart</span>
                </button>
              </div>
            </div>
          </li>`;
      });

      html += '</ul>';

      if (showViewAll) {
        html += `<div class="cura-fs__viewall-wrap">
          <a href="${collectionUrl}" class="cura-fs__viewall button">
            ${this.escHtml(viewAllLabel)} <span aria-hidden="true">→</span>
          </a>
        </div>`;
      }

      html += '</div>';

      this.panel.innerHTML = html;
      if (window.CuraBestsellersInit) {
        window.CuraBestsellersInit(this.panel);
      }
      document.dispatchEvent(new CustomEvent('cura:content-loaded', { detail: { container: this.panel } }));
    }

    formatMoney(cents) {
      // products.json returns prices as strings already in the store's currency unit
      // e.g. "100.00" means Rs. 100, NOT paise. Do NOT divide by 100.
      const num = parseFloat(cents);
      const amount = num.toFixed(2);
      if (window.Shopify && window.Shopify.money_format) {
        return window.Shopify.money_format
          .replace('{{amount}}', amount)
          .replace('{{amount_no_decimals}}', Math.round(num))
          .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
          .replace('{{amount_no_decimals_with_comma_separator}}', String(Math.round(num)));
      }
      return 'Rs. ' + amount;
    }

    escHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('[data-cura-fs]').forEach(function (el) {
      if (!el.dataset.fsInit) {
        new CuraFindSolution(el);
        el.dataset.fsInit = 'true';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();